/** Canonical role normalization across all services -- BOTH arms, lenient and strict, on the one alias map.
 *
 * `normalizeRoleStrict` was parked a layer up for years behind a claim that it "carries a runtime dependency this leaf
 * may not take". That claim was never true. Its only non-const dependency was `lookupRole` from THIS file -- a
 * utils-to-utils value import, which `QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001` forbids only while the two halves sit in
 * different files -- and its other import, `isNonEmptyString`, is inside the const/types/zod floor already. Folding
 * the strict arm back beside the map dissolves that edge rather than routing around it, and leaves the two arms
 * sharing one alias table that cannot drift.
 *
 * The two arms DISAGREE on invalid input on purpose: lenient defaults to 'user', strict answers null so an
 * authorization decision can REJECT rather than coerce to least privilege. Import the strict one unaliased.
 */

import { DEFAULT_USER_ROLE, ROLE_HIERARCHY } from '@federa/const/auth-session';
import { USER_ROLES } from '@federa/const/auth';
import { isNonEmptyString } from '@federa/const/string-guards';
import type { UserRole } from '@federa/types/auth-role';

/** Alias map onto the canonical `USER_ROLES` SSOT. Keys are LOWERCASE only -- case variations are absorbed by `lookupRole`'s lowercase fallback. */
const ROLE_NORMALIZATION_MAP: Record<string, UserRole> = {
	admin: USER_ROLES.ADMIN,
	administrator: USER_ROLES.ADMIN,

	super_admin: USER_ROLES.SUPER_ADMIN,
	superadmin: USER_ROLES.SUPER_ADMIN,

	g4: USER_ROLES.G4,
	manager: USER_ROLES.G4,

	user: USER_ROLES.USER,
	universal: USER_ROLES.USER,

	// Guest maps to user (limited access), never its own role.
	guest: USER_ROLES.USER,
};

/** Shared by both normalizeRole and normalizeRoleStrict. Both reads are OWN-key reads: the alias map is a plain object,
 *  and a bare index read answered every name it inherits from Object.prototype -- `normalizeRoleStrict('constructor')`
 *  handed an authentication flow the Object function as a recognised role instead of null (iss-f4797a27). */
export function lookupRole(trimmed: string): UserRole | undefined {
	// The spelling as given first, then its lowercase fallback.
	for (const key of [trimmed, trimmed.toLowerCase()]) {
		if (Object.hasOwn(ROLE_NORMALIZATION_MAP, key)) return ROLE_NORMALIZATION_MAP[key];
	}
	return undefined;
}

/** Absorbs every source format (DB 'G4'/'Super_Admin', frontend 'g4', null, whitespace, wrong case) and DEFAULTS to 'user' on anything unrecognized. */
export function normalizeRole(role: string | null | undefined): UserRole {
	if (!role) return DEFAULT_USER_ROLE;

	const trimmed = typeof role === 'string' ? role.trim() : String(role);
	if (trimmed === '') return DEFAULT_USER_ROLE;

	return lookupRole(trimmed) ?? DEFAULT_USER_ROLE;
}

/** Strict role normalization: returns `null` for null, undefined, empty, or unrecognized role strings, unlike `normalizeRole` which defaults to 'user'. Use in authentication flows where invalid roles must be rejected. */
export function normalizeRoleStrict(role: string | undefined | null): UserRole | null {
	if (!isNonEmptyString(role)) {
		return null;
	}

	const trimmed = role.trim();
	if (trimmed === '') {
		return null;
	}

	return lookupRole(trimmed) ?? null;
}

export function hasRole(userRole: UserRole, requiredRole: UserRole): boolean {
	const userLevel = ROLE_HIERARCHY[userRole];
	const requiredLevel = ROLE_HIERARCHY[requiredRole];
	return userLevel >= requiredLevel;
}

/** THE authorization comparison: does a RAW held role (a DB `user_type`, a DO `rpcGetRole` answer, a cached string)
 *  meet `requiredRole`? The two sides take OPPOSITE arms, and that asymmetry is the SECURITY PROPERTY, not a style
 *  preference:
 *
 *    HELD side, LENIENT -- an unrecognized held role coerces to `DEFAULT_USER_ROLE`, i.e. LEAST privilege, so a role
 *    nobody recognises cannot clear a bar. That is fail-closed, and it is why this side keeps `normalizeRole`.
 *
 *    REQUIRED side, STRICT -- an unrecognized REQUIREMENT is a DENIAL. `normalizeRole` here instead coerces it to
 *    `user` (hierarchy level 0), which EVERY role clears: a gate that admits everyone, silently, forever.
 *
 *  THE SENTENCE THAT MADE THIS WORTH CURING AT ALL FIVE SITES RATHER THAN THE ONE REACHABLE ONE: the alias table
 *  above holds `super_admin` and `superadmin` but NOT the hyphen, while this repo is kebab-case everywhere -- so an
 *  author reaching for the STRONGEST role got the WEAKEST gate, and got it silently. Measured through the old
 *  spelling before this landed, and re-measured independently at landing: `hasRole(normalizeRole('user'),
 *  normalizeRole(r))` returned TRUE for `r` of `'super-admin'`, `'owner'`, `'hacker'`, `''` and `'   '`, against four
 *  controls that answered correctly both ways.
 *
 *  THE REPO ALREADY KNEW. This file's own header states the lenient/strict asymmetry as deliberate, and
 *  `__tests__/role-normalization-strict.test.ts` already asserts `normalizeRoleStrict('owner')` is null. The invariant
 *  was pinned one layer down and violated by the consumer that most needed it: FIVE call sites re-derived this
 *  comparison by hand and every one of them picked the wrong arm for the required side. This function exists so there
 *  is no sixth.
 *
 *  REACH, because the scale is not obvious from any one call site: 22 surfaces bind a required role explicitly (18 at
 *  G4, 4 at USER, zero above G4) while 33 take the implicit default -- `g4-auth-check`'s empty `with:`, which 31 flow
 *  leaves compose through `authGateStep`, plus `requireAdminAuth`'s whole admin-federa route surface. The comparison
 *  governs most of the gated fleet through one adapter line, so its polarity is a fleet-wide property.
 *
 *  POLARITY: the refusal is the `=== null` arm, so an unrecognized requirement DENIES, on the first such value. The
 *  inverse -- refusing only once the strict lookup SUCCEEDS and loses -- requires a non-empty result to fire, which is
 *  fail-open and never fires on the case that matters.
 *
 *  THE NULL/BLANK HELD ROLE DENIES ONLY INCIDENTALLY, and that is DEFERRED-PENDING-MEASUREMENT rather than overlooked:
 *  `normalizeRole(null)` yields level 0, so an absent held role denies against any requirement ABOVE `user` and ADMITS
 *  against a USER-tier one. The four USER-tier binds are therefore gating "is anyone there" rather than "is this
 *  caller a user" until their held-role provenance is traced. Making that denial explicit is a behaviour change at
 *  exactly those sites, so it is not bundled into a change whose measured claim is that it denies nobody who passes
 *  today. Do not read the current denial as intentional.
 *
 *  `requiredRole` is `string` rather than `UserRole` on purpose: at three of the five adopting sites it genuinely
 *  arrives unvalidated (flow input, an untyped option bag), which is exactly where the null arm has to stay live.
 *  Switching the required side to the strict arm denies NO caller that passes today -- both arms resolve through the
 *  same `lookupRole`, so the resolving set is identical and only the fallback differs. The one behaviour change is a
 *  strengthening: a whitespace-only or empty required role stops resolving to `user` and starts refusing. */
export function meetsRequiredRole(heldRole: string | null | undefined, requiredRole: string): boolean {
	const normalizedRequired = normalizeRoleStrict(requiredRole);
	if (normalizedRequired === null) return false;
	return hasRole(normalizeRole(heldRole), normalizedRequired);
}

/** G4-level access or higher (G4, admin, super_admin). Accepts RAW role strings and normalizes internally -- use this rather than `role === "G4"` or a lowercased comparison, both of which miss admin and super_admin. */
export function isAuthorizedForMissionControl(role: string | null | undefined): boolean {
	if (!role) return false;
	const normalized = normalizeRole(role);
	return hasRole(normalized, USER_ROLES.G4);
}
